import { ElementIdSchema } from '@dpem/ontology';
import * as z from 'zod';

import { PageQuerySchema, paginated } from '../common/pagination.js';
import {
  DescriptionSchema,
  IdentifierSchema,
  IsoDateTimeSchema,
  MarkdownSchema,
  ShortTextSchema,
} from '../common/primitives.js';
import { BedrockModelIdSchema } from './admin.js';

/**
 * ai-assist context — elaboration over AWS Bedrock (the only provider).
 *
 * The system prompt is fixed server-side and never composed from user data
 * (architecture §6), so the request carries a prompt *kind* plus optional author
 * guidance, never a system prompt. Output is a suggestion: the caller applies it
 * through the normal authoring command so invariants and audit still hold.
 */

export const PROMPT_KINDS = [
  'elaborate-writeup',
  'summarise-for-executive',
  'draft-template-body',
  'draft-role-responsibilities',
  'draft-agent-capabilities',
  'suggest-dependencies',
  'suggest-exit-criteria',
  'rewrite-concise',
] as const;
export const PromptKindSchema = z.enum(PROMPT_KINDS);
export type PromptKind = z.infer<typeof PromptKindSchema>;

export const ASSIST_TARGET_TYPES = [
  'activity',
  'workPractice',
  'stage',
  'phase',
  'role',
  'gate',
  'lens',
  'ceremony',
  'deliverable',
  'tool',
  'asset',
  'agent',
  'agentSkill',
  'mcpServer',
  'method',
] as const;
export const AssistTargetTypeSchema = z.enum(ASSIST_TARGET_TYPES);
export type AssistTargetType = z.infer<typeof AssistTargetTypeSchema>;

export const ASSIST_INVOCATION_STATES = [
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
  'CANCELLED',
] as const;
export const AssistInvocationStateSchema = z.enum(ASSIST_INVOCATION_STATES);
export type AssistInvocationState = z.infer<typeof AssistInvocationStateSchema>;

/** The element the assistant is writing about. Ids are ontology ids where one exists. */
export const AssistTargetSchema = z.strictObject({
  type: AssistTargetTypeSchema,
  elementId: ElementIdSchema,
  /** Present when the target lives inside a method version being authored. */
  methodVersionId: IdentifierSchema.optional(),
});
export type AssistTarget = z.infer<typeof AssistTargetSchema>;

export const ElaborateRequestSchema = z.strictObject({
  target: AssistTargetSchema,
  promptKind: PromptKindSchema,
  /** Author guidance. Untrusted model input — never merged into the system prompt. */
  guidance: DescriptionSchema.optional(),
  /** Existing body the model should improve rather than replace. */
  currentText: MarkdownSchema.optional(),
  modelId: BedrockModelIdSchema.optional(),
  maxOutputTokens: z.int().min(64).max(64_000).optional(),
});
export type ElaborateRequest = z.infer<typeof ElaborateRequestSchema>;

export const AssistUsageSchema = z.strictObject({
  inputTokens: z.int().min(0),
  outputTokens: z.int().min(0),
});
export type AssistUsage = z.infer<typeof AssistUsageSchema>;

export const ASSIST_FINISH_REASONS = ['stop', 'max_tokens', 'cancelled', 'error'] as const;
export const AssistFinishReasonSchema = z.enum(ASSIST_FINISH_REASONS);
export type AssistFinishReason = z.infer<typeof AssistFinishReasonSchema>;

export const ElaborateResponseSchema = z.strictObject({
  /** Provenance id; the authoring command that applies the text cites it. */
  invocationId: IdentifierSchema,
  text: MarkdownSchema,
  modelId: BedrockModelIdSchema,
  finishReason: AssistFinishReasonSchema,
  usage: AssistUsageSchema,
  latencyMs: z.int().min(0),
});
export type ElaborateResponse = z.infer<typeof ElaborateResponseSchema>;

/**
 * One NDJSON line of the streaming endpoint. `start` arrives first with the
 * invocation id so a caller can cancel mid-stream; exactly one of `end` or
 * `error` terminates the stream.
 */
export const AssistChunkSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('start'),
    invocationId: IdentifierSchema,
    modelId: BedrockModelIdSchema,
  }),
  z.strictObject({
    type: z.literal('delta'),
    invocationId: IdentifierSchema,
    /** Token text, appended in order. Never a whole document. */
    text: z.string().max(16_384),
    index: z.int().min(0),
  }),
  z.strictObject({
    type: z.literal('end'),
    invocationId: IdentifierSchema,
    finishReason: AssistFinishReasonSchema,
    usage: AssistUsageSchema,
    latencyMs: z.int().min(0),
  }),
  z.strictObject({
    type: z.literal('error'),
    invocationId: IdentifierSchema.nullable(),
    /** Degraded, never fatal to authoring: the UI shows "assist unavailable". */
    code: z.enum(['ASSIST_UNAVAILABLE', 'ASSIST_BUDGET_EXCEEDED', 'ASSIST_CANCELLED', 'INTERNAL']),
    message: DescriptionSchema,
  }),
]);
export type AssistChunk = z.infer<typeof AssistChunkSchema>;

export const ElaborateStreamRequestSchema = ElaborateRequestSchema;
export type ElaborateStreamRequest = z.infer<typeof ElaborateStreamRequestSchema>;
export const ElaborateStreamResponseSchema = AssistChunkSchema;
export type ElaborateStreamResponse = z.infer<typeof ElaborateStreamResponseSchema>;

export const CancelInvocationRequestSchema = z.strictObject({
  invocationId: IdentifierSchema,
});
export type CancelInvocationRequest = z.infer<typeof CancelInvocationRequestSchema>;
export const CancelInvocationResponseSchema = z.strictObject({
  invocationId: IdentifierSchema,
  state: AssistInvocationStateSchema,
  cancelledAt: IsoDateTimeSchema,
});
export type CancelInvocationResponse = z.infer<typeof CancelInvocationResponseSchema>;

export const AssistInvocationSchema = z.strictObject({
  id: IdentifierSchema,
  target: AssistTargetSchema,
  promptKind: PromptKindSchema,
  state: AssistInvocationStateSchema,
  modelId: BedrockModelIdSchema,
  usage: AssistUsageSchema,
  /** Truncated preview; the full text is applied by the caller, not stored here. */
  preview: z.string().max(2_000).nullable(),
  errorMessage: DescriptionSchema.nullable(),
  /** Whether an author accepted the suggestion — the provenance that matters. */
  applied: z.boolean(),
  requestedBy: ShortTextSchema,
  startedAt: IsoDateTimeSchema,
  finishedAt: IsoDateTimeSchema.nullable(),
});
export type AssistInvocation = z.infer<typeof AssistInvocationSchema>;

export const ListInvocationsRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  methodVersionId: IdentifierSchema.optional(),
  elementId: ElementIdSchema.optional(),
  state: AssistInvocationStateSchema.optional(),
  promptKind: PromptKindSchema.optional(),
});
export type ListInvocationsRequest = z.infer<typeof ListInvocationsRequestSchema>;
export const ListInvocationsResponseSchema = z.strictObject({
  ...paginated(AssistInvocationSchema).shape,
  /** Remaining daily budget, so the UI can warn before the model refuses. */
  invocationsRemainingToday: z.int().min(0).nullable(),
});
export type ListInvocationsResponse = z.infer<typeof ListInvocationsResponseSchema>;
